/**
 * La rejilla del día (spec 015 · FR-001…FR-006).
 *
 * QUÉ ERA ESTO ANTES, Y POR QUÉ NO SERVÍA
 * ───────────────────────────────────────
 * Una **lista de filas de 30 minutos** donde cada cita era una pegatina dentro
 * de la fila en la que empezaba. Consecuencias, todas dichas por el propietario
 * al usarlo:
 *
 *   · una cita de una hora ocupaba lo mismo que una de quince minutos;
 *   · no había columnas, así que un negocio con un profesional gastaba el mismo
 *     ancho que uno con cuatro;
 *   · las horas eran 8–21 escritas a mano, no las del negocio;
 *   · y un día sin citas no pintaba rejilla, sino la frase «sin citas» — con lo
 *     que «hoy no viene nadie» y «esto no ha cargado» volvían a parecerse.
 *
 * Ahora es una rejilla de verdad: **el eje vertical es tiempo** y una caja mide
 * lo que dura. Toda la aritmética vive en `calendario-geometria.ts` y aquí solo
 * se pinta — si se calcula una posición en este fichero, está mal.
 *
 * LO QUE SE CONSERVA
 * ──────────────────
 * Arrastrar para mover. Pinchar la caja abre el recuadro con las
 * acciones (mover, cancelar). La ficha —nombre, teléfono, servicio,
 * hora— va DENTRO del bloque, también en media hora (spec 015 · FR-031).
 * Si no cabe, el hover expande; no se pinta por fuera.
 */
import { useState, type DragEvent } from 'react'

import { DetalleCita } from './calendario-detalle'
import { Bloqueo, type BloqueoCalendario } from './calendario-bloqueo'
import type { Propuesta } from './calendario-propuestas'
import {
    aHhmm,
    altoDe,
    cajaDe,
    horasDe,
    minutoDeY,
    PX_POR_HORA,
    repartirSolapes,
    type HorarioVisible,
} from './calendario-geometria'

export const DND_MIME = 'application/x-agendify-cita'

export interface CitaCalendario {
    id: number
    inicio: string
    fin: string
    minutos: number
    profesionalId: number | null
    servicioId: number | null
    esTest: boolean
    /** Está movida en pantalla y todavía NO escrita (FR-024). */
    propuesta?: boolean | undefined
    cliente?: { nombre: string; apellidos: string; telefono: string } | null
}

export function rotuloCliente(c: CitaCalendario): string {
    const x = c.cliente
    if (!x) return ''
    const apellidos = x.apellidos.trim() === '-' ? '' : x.apellidos
    return `${x.nombre} ${apellidos}`.trim()
}

/** Lo que se lee en el bloque, también si mide 30 minutos (015 · FR-031). */
export function lineasFichaCita(
    c: CitaCalendario,
    nombreServ: (id: number | null) => string,
): { hora: string; quien: string; telefono: string; servicio: string } {
    return {
        hora: c.inicio,
        quien: rotuloCliente(c),
        telefono: c.cliente?.telefono?.trim() ?? '',
        servicio: nombreServ(c.servicioId),
    }
}

export type PayloadDnD = { id: number; minutos: number; inicio: string; profesionalId: number | null; dia: string }

export type { BloqueoCalendario }

/** Una columna de la rejilla: un profesional, un día, o «todos juntos». */
export interface Columna {
    clave: string
    titulo: string
    /** Segunda línea de la cabecera. La semana la usa para el número del día. */
    subtitulo?: string | undefined
    citas: CitaCalendario[]
    /** Los huecos retenidos que caen en esta columna (FR-022). */
    bloqueos?: BloqueoCalendario[] | undefined
    /**
     * A qué día pertenece esta columna.
     *
     * Solo lo usa la vista de semana, donde cada columna es un día distinto: sin
     * esto, arrastrar una cita del martes al jueves la movería de hora **dentro
     * del martes** y la pantalla la pintaría en su sitio nuevo. Un movimiento que
     * se ve bien y no ocurrió.
     */
    dia?: string | undefined
    /** Se pinta apagada. La semana marca así los días que el negocio no abre. */
    apagada?: boolean | undefined
    /**
     * De qué profesional es la columna, cuando lo es.
     *
     * Es lo que permite mover una cita DE PROFESIONAL arrastrándola a otra
     * columna. Sin esto solo se podía cambiar la hora.
     */
    profesionalId?: number | null | undefined
    /** Spec 044 · vigentes en esta columna (lo pintado que aún no ha empezado). */
    vigentes?: number | undefined
}

export function RejillaCalendario({
    accountId,
    dia,
    columnas,
    horario,
    nombreProf,
    nombreServ,
    /** El color del profesional de una cita (FR-020). Lo reparte el servidor. */
    colorDe,
    /** Soltar una cita propone moverla; no escribe (FR-024). */
    alProponer,
    /** Un servicio elegido arriba enciende el crear-pinchando (FR-012). */
    duracionAlPinchar,
    alPinchar,
}: {
    accountId: string
    dia: string
    columnas: Columna[]
    horario: HorarioVisible
    nombreProf: (id: number | null) => string
    nombreServ: (id: number | null) => string
    colorDe: (id: number | null) => string
    alProponer?: ((p: Propuesta) => void) | undefined
    duracionAlPinchar?: number | undefined
    alPinchar?: ((columna: string, hhmm: string) => void) | undefined
}) {
    const [draggingId, setDraggingId] = useState<number | null>(null)
    const [fantasma, setFantasma] = useState<{ columna: string; min: number } | null>(null)
    const [detalle, setDetalle] = useState<{ cita: CitaCalendario; dia: string } | null>(null)

    const alto = altoDe(horario)
    const horas = horasDe(horario)

    /** El minuto al que apunta el ratón dentro de una columna. */
    const minutoDeEvento = (e: { clientY: number; currentTarget: HTMLElement }): number => {
        const caja = e.currentTarget.getBoundingClientRect()
        return minutoDeY(e.clientY - caja.top, horario)
    }

    /**
     * Soltar NO escribe: PROPONE (FR-024).
     *
     * Antes esto llamaba a la API y movía la cita de verdad, así que un arrastre
     * torpe ya había escrito en la agenda. Ahora deja la cita en su sitio nuevo,
     * apagada, y quien decide es el botón «Guardar».
     */
    const onDrop = (e: DragEvent<HTMLDivElement>, col: Columna) => {
        e.preventDefault()
        setFantasma(null)
        setDraggingId(null)
        const raw = e.dataTransfer.getData(DND_MIME) || e.dataTransfer.getData('text/plain')
        if (!raw) return
        let payload: PayloadDnD
        try {
            payload = JSON.parse(raw) as PayloadDnD
        } catch {
            return
        }
        const hhmm = aHhmm(minutoDeEvento({ clientY: e.clientY, currentTarget: e.currentTarget }))
        alProponer?.({
            citaId: payload.id,
            dia: col.dia ?? dia,
            inicio: hhmm,
            // En una columna de profesional, soltar ahí CAMBIA de profesional. En
            // una columna de día o de «todos», el profesional no se toca.
            profesionalId: col.profesionalId !== undefined ? col.profesionalId : payload.profesionalId,
            minutos: payload.minutos,
            origen: { dia: payload.dia, inicio: payload.inicio.slice(0, 5), profesionalId: payload.profesionalId },
        })
    }

    return (
        <div>
            {/* FR-004 · el ancho lo decide cuánta gente hay. Con una columna, la
                rejilla no pasa de un tercio de la página; con varias, se reparte
                lo que haga falta. */}
            <div
                className={
                    'overflow-x-auto overflow-y-auto rounded-lg border border-neutral-800 bg-neutral-950/40 ' +
                    (columnas.length === 1 ? 'max-w-md' : '')
                }
            >
                {/* Cabecera de columnas */}
                {columnas.length > 1 && (
                    <div className="flex border-b border-neutral-800">
                        <div className="w-14 shrink-0 border-r border-neutral-900" />
                        {columnas.map((col) => (
                            <div
                                key={col.clave}
                                className={
                                    'min-w-[5.5rem] flex-1 border-r border-neutral-900 px-2 py-1.5 text-center text-[11px] last:border-r-0 ' +
                                    (col.apagada ? 'bg-neutral-800/70 text-neutral-500' : 'font-medium text-neutral-300')
                                }
                                title={col.apagada ? `${col.titulo} · cerrado` : col.titulo}
                            >
                                <div className="truncate">{col.titulo}</div>
                                {col.subtitulo && (
                                    <div className="truncate text-[10px] font-normal text-neutral-500">
                                        {col.subtitulo}
                                    </div>
                                )}
                                {typeof col.vigentes === 'number' && (
                                    <div
                                        className="tabular-nums text-[10px] font-normal text-sky-300/90"
                                        data-testid={`reservas-col-${col.clave}`}
                                    >
                                        {col.vigentes}
                                    </div>
                                )}
                                {col.apagada && (
                                    <div className="text-[10px] font-normal uppercase tracking-wide text-neutral-500">
                                        cerrado
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* El margen de arriba NO es estética: la etiqueta de la primera
                    hora va centrada sobre su raya (`-translate-y-1/2`), así que a
                    `top: 0` la mitad de «09:00» queda por encima del contenedor —
                    y el contenedor recorta. Sin este hueco, la primera hora del
                    negocio se lee cortada por la mitad.

                    Va en MARGEN y no en relleno a propósito: el relleno entraría
                    dentro del alto y comprimiría la rejilla, con lo que una hora
                    dejaría de medir una hora. */}
                <div className="flex" style={{ height: `${alto}px`, marginTop: 10, marginBottom: 6 }}>
                    {/* Las horas */}
                    <div className="relative w-14 shrink-0 border-r border-neutral-900">
                        {horas.map((m) => (
                            <div
                                key={m}
                                className="absolute right-1 -translate-y-1/2 text-[11px] tabular-nums text-neutral-500"
                                style={{ top: `${((m - horario.desdeMin) / 60) * PX_POR_HORA}px` }}
                            >
                                {aHhmm(m)}
                            </div>
                        ))}
                    </div>

                    {columnas.map((col) => (
                        <div
                            key={col.clave}
                            data-testid={`columna-${col.clave}`}
                            className={
                                'relative min-w-[5.5rem] flex-1 border-r border-neutral-900 last:border-r-0 ' +
                                (col.apagada ? 'bg-neutral-800/70' : '')
                            }
                            onDragOver={(e) => {
                                e.preventDefault()
                                e.dataTransfer.dropEffect = 'move'
                                setFantasma({ columna: col.clave, min: minutoDeEvento(e) })
                            }}
                            onDragLeave={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) setFantasma(null)
                            }}
                            onDrop={(e) => onDrop(e, col)}
                            onMouseMove={(e) => {
                                if (duracionAlPinchar === undefined) return
                                setFantasma({ columna: col.clave, min: minutoDeEvento(e) })
                            }}
                            onMouseLeave={() => {
                                if (duracionAlPinchar !== undefined) setFantasma(null)
                            }}
                            onClick={(e) => {
                                if (duracionAlPinchar === undefined || !alPinchar) return
                                alPinchar(col.clave, aHhmm(minutoDeEvento(e)))
                            }}
                            style={duracionAlPinchar !== undefined ? { cursor: 'copy' } : undefined}
                        >
                            {/* FR-003 · las rayas de las horas se pintan SIEMPRE,
                                haya citas o no. Es lo que distingue un día libre
                                de una pantalla que no cargó. */}
                            {horas.map((m) => (
                                <div
                                    key={m}
                                    className="pointer-events-none absolute inset-x-0 border-t border-neutral-900"
                                    style={{ top: `${((m - horario.desdeMin) / 60) * PX_POR_HORA}px` }}
                                />
                            ))}
                            {/* La media hora, más tenue: ayuda a leer sin competir. */}
                            {horas.slice(0, -1).map((m) => (
                                <div
                                    key={`m${m}`}
                                    className="pointer-events-none absolute inset-x-0 border-t border-neutral-900/40"
                                    style={{ top: `${((m + 30 - horario.desdeMin) / 60) * PX_POR_HORA}px` }}
                                />
                            ))}

                            {/* El hueco que se va a ocupar (FR-012). */}
                            {fantasma?.columna === col.clave && (
                                <Fantasma
                                    min={fantasma.min}
                                    minutos={duracionAlPinchar ?? 30}
                                    horario={horario}
                                />
                            )}

                            {/* FR-023 · los bloqueos van DEBAJO de las citas, y
                                a propósito: si un hueco retenido y una cita se
                                pisaran, lo que manda es la cita — es lo que
                                existe de verdad. */}
                            {(col.bloqueos ?? []).map((b) => (
                                <Bloqueo key={`${b.dia}|${b.inicio}|${b.profesionalId}`} b={b} horario={horario} />
                            ))}

                            {repartirSolapes(col.citas).map(({ cita, carril, carriles }) => {
                                const caja = cajaDe(cita.inicio, cita.minutos, horario)
                                if (!caja) return null
                                return (
                                    <div
                                        key={cita.id}
                                        className="group/cita absolute z-[2] overflow-hidden px-0.5 hover:z-30 hover:!h-auto hover:overflow-visible focus-within:z-30 focus-within:!h-auto focus-within:overflow-visible"
                                        style={{
                                            top: `${caja.arriba}px`,
                                            height: `${caja.alto}px`,
                                            left: `${(carril / carriles) * 100}%`,
                                            width: `${100 / carriles}%`,
                                        }}
                                    >
                                        <Tarjeta
                                            dia={col.dia ?? dia}
                                            c={cita}
                                            dragging={draggingId === cita.id}
                                            nombreServ={nombreServ}
                                            color={colorDe(cita.profesionalId)}
                                            onDragStart={() => setDraggingId(cita.id)}
                                            onDragEnd={() => {
                                                setDraggingId(null)
                                                setFantasma(null)
                                            }}
                                            alAbrir={() => setDetalle({ cita, dia: col.dia ?? dia })}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                </div>
            </div>
            {detalle && (
                <DetalleCita
                    accountId={accountId}
                    dia={detalle.dia}
                    c={detalle.cita}
                    nombreProf={nombreProf}
                    nombreServ={nombreServ}
                    color={colorDe(detalle.cita.profesionalId)}
                    alCerrar={() => setDetalle(null)}
                />
            )}
        </div>
    )
}

/** El bloque que se va a ocupar, con su tamaño real (FR-012). */
function Fantasma({
    min,
    minutos,
    horario,
}: {
    min: number
    minutos: number
    horario: HorarioVisible
}) {
    const caja = cajaDe(aHhmm(min), minutos, horario)
    if (!caja) return null
    return (
        <div
            className="pointer-events-none absolute inset-x-0 rounded border border-sky-400/60 bg-sky-500/20"
            style={{ top: `${caja.arriba}px`, height: `${caja.alto}px` }}
        >
            <span className="px-1 text-[10px] tabular-nums text-sky-200">
                {aHhmm(min)}–{aHhmm(min + minutos)}
            </span>
        </div>
    )
}

function Tarjeta({
    dia,
    c,
    dragging,
    nombreServ,
    color,
    onDragStart,
    onDragEnd,
    alAbrir,
}: {
    dia: string
    c: CitaCalendario
    dragging: boolean
    nombreServ: (id: number | null) => string
    color: string
    onDragStart: () => void
    onDragEnd: () => void
    alAbrir: () => void
}) {
    // Spec 015 · FR-031: nombre, teléfono, servicio y hora van DENTRO
    // del bocadillo, también en 30 minutos. Si una línea no cabe, se
    // queda dentro; el hover (o el foco) expande y se lee entero.
    const ficha = lineasFichaCita(c, nombreServ)
    const linea =
        'min-w-0 truncate leading-none group-hover/cita:whitespace-normal group-hover/cita:break-words group-focus-within/cita:whitespace-normal group-focus-within/cita:break-words'

    return (
        <div
            data-testid="cita"
            role="button"
            tabIndex={0}
            draggable
            onDragStart={(e) => {
                const payload: PayloadDnD = {
                    id: c.id,
                    minutos: c.minutos,
                    inicio: c.inicio,
                    profesionalId: c.profesionalId,
                    // De dónde viene DE VERDAD. Con el día de la página, arrastrar
                    // una cita del jueves y devolverla a su sitio dejaba una
                    // propuesta fantasma: `esInmovil` comparaba jueves con lunes.
                    dia,
                }
                e.dataTransfer.setData(DND_MIME, JSON.stringify(payload))
                e.dataTransfer.setData('text/plain', JSON.stringify(payload))
                e.dataTransfer.effectAllowed = 'move'
                onDragStart()
            }}
            onDragEnd={onDragEnd}
            onClick={(e) => {
                e.stopPropagation()
                if (dragging) return
                alAbrir()
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    alAbrir()
                }
            }}
            className={
                'flex h-full min-h-0 cursor-pointer flex-col overflow-hidden rounded border-l-[3px] border-y border-r px-1 py-px text-[10px] text-neutral-100 shadow-black/50 group-hover/cita:h-auto group-hover/cita:min-h-full group-hover/cita:overflow-visible group-hover/cita:!bg-neutral-900 group-hover/cita:shadow-lg group-focus-within/cita:h-auto group-focus-within/cita:min-h-full group-focus-within/cita:overflow-visible group-focus-within/cita:!bg-neutral-900 active:cursor-grabbing ' +
                (dragging ? 'opacity-50 ' : '') +
                (c.propuesta ? 'border-dashed opacity-70' : '')
            }
            style={{
                borderLeftColor: color,
                borderTopColor: `${color}55`,
                borderRightColor: `${color}55`,
                borderBottomColor: `${color}55`,
                backgroundColor: `${color}26`,
            }}
        >
            <div className={`flex items-baseline gap-1 ${linea}`}>
                <span className="shrink-0 tabular-nums font-medium">{ficha.hora}</span>
                {c.esTest && (
                    <span className="shrink-0" title="La creó el laboratorio de pruebas, no una persona">
                        🧪
                    </span>
                )}
            </div>
            {ficha.quien ? <div className={`${linea} text-neutral-100`}>{ficha.quien}</div> : null}
            {ficha.telefono ? <div className={`${linea} tabular-nums text-neutral-300`}>{ficha.telefono}</div> : null}
            {ficha.servicio ? <div className={`${linea} text-neutral-400`}>{ficha.servicio}</div> : null}
        </div>
    )
}
